import { Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {BehaviorSubject, map, Observable, take} from 'rxjs';
import { AuthService } from "./auth.service";
import {filter} from "rxjs/operators";

type ResourcePath =
    | 'task'
    | 'product-library'
    | 'production-data'
    | 'prepress-jobentry'
    | 'product-development'
    | 'prepress-dashboard'
    | 'ink-kitchen';

type FlagKey =
    | 'canAccessTask'
    | 'canAccessProductLibrary'
    | 'canAccessProductionData'
    | 'canAccessPrepressJobentry'
    | 'canAccessProductDevelopment'
    | 'canAccessPrePressDashboard'
    | 'canAccessInkKitchen'
    | 'canCreateTask'
    | 'canCreatePrepressDashboardTask'
    | 'canMarketingPrePressDashboard';

type FlagSignals = {
  [K in FlagKey]: Signal<boolean>;
};

interface Permission {
  resourceId: number;
  resourceName: string;
  resourceType: string;
  action: string;
  resourcePath: ResourcePath;
  ability: 'can' | 'cannot';
  context: string;
  type: 'full' | 'context';
}

@Injectable({
  providedIn: 'root',
})
export class PermissionFlagsService {

  private permissionConfig: Record<ResourcePath, { flag: FlagKey }> = {
    'task':                { flag: 'canAccessTask' },
    'product-library':     { flag: 'canAccessProductLibrary' },
    'production-data':     { flag: 'canAccessProductionData' },
    'prepress-jobentry':   { flag: 'canAccessPrepressJobentry' },
    'product-development': { flag: 'canAccessProductDevelopment' },
    'prepress-dashboard':  { flag: 'canAccessPrePressDashboard' },
    'ink-kitchen':         { flag: 'canAccessInkKitchen' },
  };

  private permissionsSubject = new BehaviorSubject<Permission[]>([]);
  permissions$ = this.permissionsSubject.asObservable();

  readonly flags: FlagSignals = {
    ...Object.fromEntries(
        (Object.keys(this.permissionConfig) as ResourcePath[]).map((resourcePath) => {
          const flag = this.permissionConfig[resourcePath].flag;
          return [
            flag,
            toSignal(
                this.permissions$.pipe(
                    map((permissions) => this.calculateFlagValue(resourcePath, permissions))
                ),
                { initialValue: false }
            )
          ];
        })
    ),
    canCreateTask: toSignal(
        this.permissions$.pipe(
            map((permissions) => {
              const taskPermission = permissions.find(
                  p => p.resourcePath === 'task' && p.ability === 'can'
              );
              return taskPermission?.context === 'CreateTask';
            })
        ),
        { initialValue: false }
    ),
    canCreatePrepressDashboardTask: toSignal(
        this.permissions$.pipe(
            map((permissions) => {
              const match = permissions.find(
                  p => p.resourcePath === 'prepress-dashboard' && p.ability === 'can'
              );
              return match?.context === 'CreateTask';
            })
        ),
        { initialValue: false }
    ),
      canMarketingPrePressDashboard: toSignal(
          this.permissions$.pipe(
              map((permissions) => {
                  const match = permissions.find(
                      p => p.resourcePath === 'prepress-dashboard' && p.ability === 'can'
                  );
                  return match?.context === 'Marketing';
              })
          ),
          { initialValue: false }
      ),
  } as FlagSignals;


  constructor(private auth: AuthService) {
    this.loadAndUpdatePermissions();
  }

  private calculateFlagValue(resourcePath: ResourcePath, permissions: Permission[]): boolean {
    // Temporary workaround: ink-kitchen is currently unauthenticated and not returned by /auth/login
    // if (resourcePath === 'ink-kitchen') {
    //   return true;
    // }

    if (permissions.length === 0) return false;

    const match = permissions.find(p => p.resourcePath === resourcePath);
    if (!match) return false;

    return match.ability === 'can';
  }

  loadAndUpdatePermissions(): void {
    const userInfoStr = sessionStorage.getItem(`${this.auth.APP_NAMESPACE}_user_info`);
    if (userInfoStr) {
      try {
        const userInfo = JSON.parse(userInfoStr);
        if (userInfo?.permissions) {
          this.permissionsSubject.next(userInfo.permissions as Permission[]);
        }
      } catch (e) {
        console.error('Failed to parse user permissions', e);
      }
    }
  }

    refreshPermissions(): Observable<Permission[]> {
        this.loadAndUpdatePermissions();
        return this.permissions$.pipe(
            filter(p => p.length > 0),
            take(1)
        );
    }
}